/* ============================================================
 * ADG Reviews — sections/adg-reviews.liquid
 *
 * Two rows, one card component, one carousel:
 *   [data-adg-rv-row="google"]  data-src = JSON feed, schema "adg-reviews/v1"
 *   [data-adg-rv-row="store"]   Growave storefront SDK (window.gw.sdk)
 *
 * Both rows load together and render the SAME number of cards
 * (min of the setting and what each source returned), so neither row
 * looks longer. A row whose source fails: Google row is hidden; store row
 * keeps its server-rendered header and drops the cards.
 *
 * Carousel = WAI-ARIA APG carousel pattern on a native scroll-snap track:
 * region + aria-roledescription="carousel", slides are groups "n of N",
 * real Previous/Next buttons, off-screen slides are inert (focus never
 * lands on a hidden card), optional autoplay with a visible pause button
 * that stops on hover and on keyboard focus and never starts under
 * prefers-reduced-motion. Review text is rendered with textContent only,
 * unedited; the clamp is CSS.
 *
 * Two looks (root data-style): "datamax" (default; header "<Google> Reviews
 * 5.0 ★★★★★ (1,030)", reviewer block on top of the card with a badge on the
 * photo: the Google G on Google cards, the ADG seal on store cards) and
 * "strict" (Google brand-page wording, reviewer block at the bottom, no
 * badge). data-logos="0" removes every Google logo.
 * ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var STAR = 'M12 2.4l2.95 6.1 6.65.8-4.9 4.65 1.25 6.6L12 17.35 6.05 20.6l1.25-6.6L2.4 9.35l6.65-.8z';
  /* Initial-tile colors: every one is at least 4.5:1 against white text. */
  var TILE = ['#7a4e2d', '#2f5d62', '#5b3f8c', '#9b2c2c', '#2d5a8c', '#4f6b2a', '#7a5c00', '#3d4f5c'];
  var GROWAVE_WAIT_MS = 8000;
  var uidSeq = 0;

  var rtf = (window.Intl && Intl.RelativeTimeFormat) ? new Intl.RelativeTimeFormat('en', { numeric: 'always' }) : null;
  var nf = (window.Intl && Intl.NumberFormat) ? new Intl.NumberFormat('en-US') : null;

  /* ---------------------------------------------------------------- utils */
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function svgStar(on) {
    var ns = 'http://www.w3.org/2000/svg';
    var s = document.createElementNS(ns, 'svg');
    s.setAttribute('viewBox', '0 0 24 24');
    s.setAttribute('aria-hidden', 'true');
    s.setAttribute('focusable', 'false');
    s.setAttribute('class', 'adg-reviews__star' + (on ? ' adg-reviews__star--on' : ''));
    var p = document.createElementNS(ns, 'path');
    p.setAttribute('d', STAR);
    s.appendChild(p);
    return s;
  }
  function fmtNum(n) { return nf ? nf.format(n) : String(n); }
  function relTime(iso, fallback) {
    var t = Date.parse(iso);
    if (isNaN(t)) return fallback || '';
    var diff = (t - Date.now()) / 1000;
    var units = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
    for (var i = 0; i < units.length; i++) {
      if (Math.abs(diff) >= units[i][1]) {
        var v = Math.trunc(diff / units[i][1]);
        return rtf ? rtf.format(v, units[i][0]) : (Math.abs(v) + ' ' + units[i][0] + (Math.abs(v) === 1 ? '' : 's') + ' ago');
      }
    }
    return 'just now';
  }
  function longDate(iso) {
    var t = Date.parse(iso);
    if (isNaN(t)) return '';
    return new Date(t).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }
  /* "2026-10-06" -> "Oct 6, 2026" in local time (no UTC day shift). */
  function asOfDate(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || '');
    if (!m) return '';
    return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  function hash(s) {
    var h = 0;
    for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h);
  }
  function settle(promises) {
    return Promise.all(promises.map(function (p) {
      return p.then(function (v) { return { ok: true, value: v }; }, function (e) { return { ok: false, error: e }; });
    }));
  }

  /* ---------------------------------------------------------------- sources */
  function loadGoogle(row, cfg) {
    var src = row.getAttribute('data-src');
    if (!src) return Promise.reject(new Error('no data url'));
    return fetch(src, { credentials: 'omit', headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (feed) {
        if (!feed || !Array.isArray(feed.reviews)) throw new Error('bad feed');
        googleHeader(row, feed, cfg);
        return feed.reviews.map(function (r) {
          return {
            name: r.name || 'A Google user',
            photo: r.photo || null,
            rating: Math.round(+r.rating || 0),
            text: typeof r.text === 'string' ? r.text : '',
            time: r.time,
            relative: r.relative_time,
            translated: !!r.translated
          };
        });
      });
  }

  function googleHeader(row, feed, cfg) {
    var sum = row.querySelector('[data-adg-rv-summary]');
    if (sum && cfg.style === 'datamax') {
      // Datamax lockup: big rating, stars beside it, (count) linked to the
      // Google listing. The visually hidden words make it read as a sentence.
      sum.textContent = '';
      if (feed.rating != null) {
        sum.appendChild(el('strong', 'adg-reviews__score', (+feed.rating).toFixed(1)));
        var hs = el('span', 'adg-reviews__hstars');
        hs.setAttribute('aria-hidden', 'true');
        for (var k = 1; k <= 5; k++) hs.appendChild(svgStar(k <= Math.round(+feed.rating)));
        sum.appendChild(hs);
        sum.appendChild(el('span', 'visually-hidden', ' out of 5 stars on Google, '));
      }
      if (feed.count != null) {
        var href = row.getAttribute('data-profile') || feed.profile_url;
        var countText = '(' + fmtNum(+feed.count) + ')';
        var c = href ? el('a', 'adg-reviews__count') : el('span', 'adg-reviews__count');
        c.appendChild(document.createTextNode(countText));
        c.appendChild(el('span', 'visually-hidden', ' ' + (+feed.count === 1 ? 'review' : 'reviews') + (href ? ', see all on Google (opens in a new tab)' : '')));
        if (href) { c.href = href; c.target = '_blank'; c.rel = 'noopener'; }
        sum.appendChild(c);
      }
      if (cfg.minRating > 1) sum.appendChild(el('span', 'adg-reviews__selected', ' · Showing selected reviews'));
    } else if (sum) {
      sum.textContent = '';
      // Wording per Google's "Customer reviews" brand page: "on Google", an
      // "as of" date, and no stars next to the G or the word Google.
      if (feed.rating != null) {
        sum.appendChild(el('strong', 'adg-reviews__score', (+feed.rating).toFixed(1)));
        sum.appendChild(document.createTextNode(' on Google'));
      } else {
        sum.appendChild(document.createTextNode('On Google'));
      }
      if (feed.count != null) sum.appendChild(document.createTextNode(' · ' + fmtNum(+feed.count) + (+feed.count === 1 ? ' review' : ' reviews')));
      var d = asOfDate(feed.as_of);
      if (d) {
        sum.appendChild(document.createTextNode(' · '));
        sum.appendChild(el('span', 'adg-reviews__nowrap', 'as of ' + d));
      }
      if (cfg.minRating > 1) sum.appendChild(el('span', 'adg-reviews__selected', ' · Showing selected reviews'));
    }
    row.querySelectorAll('[data-adg-rv-profile]').forEach(function (a) {
      if (!a.getAttribute('href') && feed.profile_url) a.setAttribute('href', feed.profile_url);
      if (!a.getAttribute('href')) a.hidden = true;
    });
    row.querySelectorAll('[data-adg-rv-write]').forEach(function (a) {
      if (!a.getAttribute('href') && feed.write_review_url) a.setAttribute('href', feed.write_review_url);
      if (!a.getAttribute('href')) a.hidden = true;
    });
  }

  function waitForGrowave(ms) {
    return new Promise(function (resolve, reject) {
      var done = false, poll;
      function finish(ok, v) {
        if (done) return;
        done = true;
        clearInterval(poll);
        document.removeEventListener('gwSdkReady', attempt);
        ok ? resolve(v) : reject(v);
      }
      // The reviews module is attached lazily: sdk.reviews does not exist
      // until runIfModuleReady(REVIEWS) has fired (observed on ADG live,
      // 2026-10-06), so ask for the module first and test for it after.
      var asked = false;
      function ready(sdk) {
        if (sdk.reviews && sdk.reviews.methods && typeof sdk.reviews.methods.getReviews === 'function') finish(true, sdk);
      }
      function attempt() {
        var sdk = window.gw && window.gw.sdk;
        if (!sdk) return;
        var mod = sdk.common && sdk.common.constants && sdk.common.constants.modules && sdk.common.constants.modules.REVIEWS;
        if (!asked && typeof sdk.runIfModuleReady === 'function' && mod) {
          asked = true;
          try { sdk.runIfModuleReady(mod, function () { ready(sdk); }); } catch (e) { /* fall through to polling */ }
        }
        ready(sdk);
      }
      document.addEventListener('gwSdkReady', attempt);
      poll = setInterval(attempt, 400);
      attempt();
      setTimeout(function () { finish(false, new Error('Growave SDK not available')); }, ms);
    });
  }

  function loadStore(row, cfg) {
    var sort = row.getAttribute('data-sort') === 'highestRating' ? 'highestRating' : 'mostRecent';
    return waitForGrowave(GROWAVE_WAIT_MS).then(function (sdk) {
      return sdk.reviews.methods.getReviews({
        perPage: Math.min(50, cfg.count * 3),
        sortingOption: sort,
        onlyPublishedReviews: true
      });
    }).then(function (res) {
      var items = (res && res.result && res.result.items) || (res && res.items) || (Array.isArray(res) ? res : null);
      if (!items) throw new Error('no items');
      return items.map(function (r) {
        var who = (r.customer && r.customer.name) || r.customerDisplayName || 'Customer';
        return {
          name: who,
          photo: null,
          rating: Math.round(+(r.rating != null ? r.rating : r.rate) || 0),
          title: r.title || '',
          text: typeof r.body === 'string' ? r.body : '',
          time: r.createdAt,
          productId: r.product && r.product.id
        };
      });
    });
  }

  /* Product title + link for store cards: one tokenless Storefront API
     query for every product id in the row. Optional: on any failure the
     cards simply show no product line. */
  function attachProducts(reviews) {
    var ids = [];
    reviews.forEach(function (r) { if (r.productId && ids.indexOf(r.productId) < 0) ids.push(r.productId); });
    if (!ids.length || !window.fetch) return Promise.resolve(reviews);
    var q = 'query($ids:[ID!]!){nodes(ids:$ids){... on Product{id title onlineStoreUrl handle}}}';
    return fetch('/api/2025-07/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: q, variables: { ids: ids.map(function (id) { return 'gid://shopify/Product/' + id; }) } })
    }).then(function (r) { return r.ok ? r.json() : null; }).then(function (j) {
      var map = {};
      ((j && j.data && j.data.nodes) || []).forEach(function (p) {
        if (p && p.id && p.title) map[p.id.split('/').pop()] = { title: p.title, url: p.onlineStoreUrl || ('/products/' + p.handle) };
      });
      reviews.forEach(function (r) { if (r.productId && map[r.productId]) r.product = map[r.productId]; });
      return reviews;
    }).catch(function () { return reviews; });
  }

  /* ---------------------------------------------------------------- cards */
  function buildSlide(r, i, n, kind, cfg, uid) {
    var slide = el('div', 'adg-reviews__slide');
    slide.setAttribute('role', 'group');
    slide.setAttribute('aria-roledescription', 'slide');
    slide.setAttribute('aria-label', (i + 1) + ' of ' + n);

    var card = el('article', 'adg-reviews__card');
    var dm = cfg.style === 'datamax';
    var by = reviewer(r, kind, cfg);
    if (dm) card.appendChild(by);

    var stars = el('div', 'adg-reviews__stars');
    stars.appendChild(el('span', 'visually-hidden', 'Rated ' + r.rating + ' out of 5'));
    for (var k = 1; k <= 5; k++) stars.appendChild(svgStar(k <= r.rating));
    card.appendChild(stars);

    var body = el('div', 'adg-reviews__body');
    var p = el('p', 'adg-reviews__text');
    p.id = uid + '-t' + i;
    if (r.title) {
      p.appendChild(el('strong', null, r.title));
      p.appendChild(document.createTextNode('\n'));
    }
    p.appendChild(document.createTextNode(r.text));
    body.appendChild(p);
    card.appendChild(body);

    var slot = el('div', 'adg-reviews__more-slot');
    var more = el('button', 'adg-reviews__more is-unneeded');
    more.type = 'button';
    more.setAttribute('aria-expanded', 'false');
    more.setAttribute('aria-controls', p.id);
    var label = el('span', null, 'Read more');
    more.appendChild(label);
    // One name (no stray space from joined spans), starting with the visible text.
    more.setAttribute('aria-label', 'Read more, review by ' + r.name);
    more.addEventListener('click', function () {
      var open = card.classList.toggle('is-expanded');
      more.setAttribute('aria-expanded', open ? 'true' : 'false');
      label.textContent = open ? 'Show less' : 'Read more';
      more.setAttribute('aria-label', (open ? 'Show less' : 'Read more') + ', review by ' + r.name);
    });
    slot.appendChild(more);
    card.appendChild(slot);

    if (!dm) card.appendChild(by);

    slide.appendChild(card);
    return slide;
  }

  /* Photo (or initial) + name + date line. Datamax style puts a badge on the
     photo: the official Google G on Google cards (only when logos are on),
     the ADG seal on store cards. Both badges are decorative; the row title
     already names the source. */
  function reviewer(r, kind, cfg) {
    var by = el('div', 'adg-reviews__by');
    var face = el('span', 'adg-reviews__face');
    face.appendChild(avatar(r, cfg));
    var badgeSrc = cfg.style !== 'datamax' ? null
      : kind === 'google' ? (cfg.logos ? cfg.gBadge : null) : cfg.storeBadge;
    if (badgeSrc) {
      var badge = el('span', 'adg-reviews__badge adg-reviews__badge--' + kind);
      badge.setAttribute('aria-hidden', 'true');
      var bi = el('img');
      bi.alt = '';
      bi.width = 52;
      bi.height = 52;
      bi.loading = 'lazy';
      bi.decoding = 'async';
      bi.src = badgeSrc;
      badge.appendChild(bi);
      face.appendChild(badge);
    }
    by.appendChild(face);
    var bt = el('div', 'adg-reviews__by-text');
    // Datamax style uses divs: the theme's gated contrast pass recolors links
    // inside <p> (body[data-adg-contrast] :is(p, …) a), which would turn the
    // grey product link orange. Strict keeps its <p> markup unchanged.
    var lineTag = cfg.style === 'datamax' ? 'div' : 'p';
    bt.appendChild(el(lineTag, 'adg-reviews__name', r.name));
    var meta = el(lineTag, 'adg-reviews__meta');
    var when = el('time', null, relTime(r.time, r.relative));
    if (r.time) { when.setAttribute('datetime', r.time); when.title = longDate(r.time); }
    meta.appendChild(when);
    if (kind === 'google' && r.translated) meta.appendChild(el('span', 'adg-reviews__meta-extra', ' · Translated by Google'));
    if (kind === 'store' && r.product) {
      meta.appendChild(document.createTextNode(' · '));
      var a = el('a', null, r.product.title);
      a.href = r.product.url;
      meta.appendChild(a);
      meta.title = r.product.title;
    }
    bt.appendChild(meta);
    by.appendChild(bt);
    return by;
  }

  function initialTile(name) {
    var t = el('span', 'adg-reviews__avatar adg-reviews__avatar--initial', (name || '?').trim().charAt(0) || '?');
    t.setAttribute('aria-hidden', 'true');
    t.style.background = TILE[hash(name || '') % TILE.length];
    return t;
  }
  function avatar(r, cfg) {
    if (!cfg.photos || !r.photo) return initialTile(r.name);
    var img = el('img', 'adg-reviews__avatar');
    img.alt = '';
    img.width = cfg.style === 'datamax' ? 40 : 44;
    img.height = img.width;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.referrerPolicy = 'no-referrer';
    img.addEventListener('error', function () { img.replaceWith(initialTile(r.name)); }, { once: true });
    img.src = r.photo;
    return img;
  }

  function checkOverflow(track) {
    track.querySelectorAll('.adg-reviews__card').forEach(function (card) {
      if (card.classList.contains('is-expanded')) return;
      var p = card.querySelector('.adg-reviews__text');
      var more = card.querySelector('.adg-reviews__more');
      if (!p || !more) return;
      more.classList.toggle('is-unneeded', p.scrollHeight <= p.clientHeight + 1);
    });
  }

  /* A text-spacing override (WCAG 1.4.12) adds a stylesheet or a style attribute without
     changing any card's size, so ResizeObserver never fires; re-check every track then. */
  var spacingWatch = null;
  function watchSpacing() {
    if (spacingWatch || !('MutationObserver' in window)) return;
    var pending = false;
    spacingWatch = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        document.querySelectorAll('[data-adg-rv-track]').forEach(checkOverflow);
      });
    });
    spacingWatch.observe(document.head, { childList: true });
    spacingWatch.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
    spacingWatch.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
  }

  /* ---------------------------------------------------------------- carousel */
  function Carousel(row, cfg) {
    var self = this;
    var track = row.querySelector('[data-adg-rv-track]');
    var carousel = row.querySelector('.adg-reviews__carousel');
    var controls = row.querySelector('[data-adg-rv-controls]');
    var prev = row.querySelector('[data-adg-rv-prev]');
    var next = row.querySelector('[data-adg-rv-next]');
    var dotsBox = row.querySelector('[data-adg-rv-dots]');
    var rotate = row.querySelector('[data-adg-rv-rotate]');
    var slides = Array.prototype.slice.call(track.children);
    var hasInert = 'inert' in HTMLElement.prototype;
    var per = 1, pages = 1, page = 0, raf = 0, settleTimer = 0, timer = 0;
    var hovering = false, playing = false;

    var status = el('p', 'visually-hidden');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    carousel.appendChild(status);

    function padL() { return parseFloat(getComputedStyle(track).paddingLeft) || 0; }
    function measure() {
      var cs = getComputedStyle(track);
      var gap = parseFloat(cs.columnGap) || 0;
      var inner = track.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      var w = slides[0] ? slides[0].offsetWidth : inner;
      per = Math.max(1, Math.round((inner + gap) / (w + gap)));
      pages = Math.max(1, Math.ceil(slides.length / per));
      if (dotsBox) {
        dotsBox.textContent = '';
        for (var i = 0; i < pages; i++) {
          var d = el('span', 'adg-reviews__dot');
          d.setAttribute('data-page', i);
          dotsBox.appendChild(d);
        }
      }
      controls && controls.classList.toggle('is-single', pages <= 1);
    }
    function firstIndexFor(p) { return Math.min(p * per, Math.max(0, slides.length - per)); }
    function goTo(p, announce) {
      p = Math.max(0, Math.min(pages - 1, p));
      var s = slides[firstIndexFor(p)];
      if (!s) return;
      track.scrollTo({ left: s.offsetLeft - padL(), behavior: REDUCED.matches ? 'auto' : 'smooth' });
      page = p;
      paint();
      if (announce) {
        var a = firstIndexFor(p) + 1, b = Math.min(slides.length, a + per - 1);
        status.textContent = a === b ? ('Showing review ' + a + ' of ' + slides.length)
          : ('Showing reviews ' + a + ' to ' + b + ' of ' + slides.length);
      }
    }
    function sync() {
      // offsetLeft is measured from the track's padding edge (the track is
      // position: relative), so the visible window is [x + pl, x + w - pr].
      var cs = getComputedStyle(track);
      var x = track.scrollLeft, pl = parseFloat(cs.paddingLeft) || 0, pr = parseFloat(cs.paddingRight) || 0;
      var max = track.scrollWidth - track.clientWidth;
      var lo = x + pl - 2, hi = x + track.clientWidth - pr + 2;
      var first = -1;
      slides.forEach(function (s, i) {
        var visible = s.offsetLeft >= lo && s.offsetLeft + s.offsetWidth <= hi;
        if (visible && first < 0) first = i;
        if (hasInert) s.inert = !visible; else s.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });
      if (x >= max - 2) page = pages - 1;
      else if (first >= 0) page = Math.min(pages - 1, Math.round(first / per));
      paint();
    }
    function paint() {
      if (prev) prev.setAttribute('aria-disabled', page <= 0 ? 'true' : 'false');
      if (next) next.setAttribute('aria-disabled', page >= pages - 1 ? 'true' : 'false');
      if (dotsBox) Array.prototype.forEach.call(dotsBox.children, function (d, i) { d.classList.toggle('is-active', i === page); });
    }
    function onScroll() {
      if (!raf) raf = requestAnimationFrame(function () { raf = 0; sync(); });
      clearTimeout(settleTimer);
      settleTimer = setTimeout(sync, 120);
    }

    /* Autoplay: never under reduced motion; stops for good on keyboard focus
       (APG), pauses while hovered or off-screen or the tab is hidden. */
    function setPlaying(on) {
      playing = on;
      clearInterval(timer);
      if (rotate) {
        rotate.setAttribute('data-state', on ? 'playing' : 'paused');
        rotate.setAttribute('aria-label', on ? 'Stop automatic slide show' : 'Start automatic slide show');
      }
      if (on) {
        timer = setInterval(function () {
          if (hovering || document.hidden || !self.inView) return;
          goTo(page >= pages - 1 ? 0 : page + 1, false);
        }, cfg.autoplay * 1000);
      }
    }

    measure();
    sync();
    track.addEventListener('scroll', onScroll, { passive: true });
    if (prev) prev.addEventListener('click', function () { if (page > 0) goTo(page - 1, true); });
    if (next) next.addEventListener('click', function () { if (page < pages - 1) goTo(page + 1, true); });
    // The track is a keyboard stop (a scroll container must be reachable,
    // WCAG 2.1.1): arrow keys page through the cards, Home/End jump.
    track.addEventListener('keydown', function (e) {
      if (e.target !== track) return;
      var to = e.key === 'ArrowRight' ? page + 1 : e.key === 'ArrowLeft' ? page - 1
        : e.key === 'Home' ? 0 : e.key === 'End' ? pages - 1 : null;
      if (to === null) return;
      e.preventDefault();
      goTo(to, true);
    });
    if (dotsBox) dotsBox.addEventListener('click', function (e) {
      var d = e.target.closest('.adg-reviews__dot');
      if (d) goTo(+d.getAttribute('data-page'), true);
    });

    this.inView = true;
    if (cfg.autoplay > 0 && rotate) {
      carousel.addEventListener('mouseenter', function () { hovering = true; });
      carousel.addEventListener('mouseleave', function () { hovering = false; });
      carousel.addEventListener('focusin', function (e) { if (e.target !== rotate && playing) setPlaying(false); });
      rotate.addEventListener('click', function () { setPlaying(!playing); });
      setPlaying(!REDUCED.matches);
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (es) { self.inView = es[0].isIntersecting; });
        this.io.observe(carousel);
      }
    }

    var lastW = track.clientWidth;
    function onResize() {
      if (track.clientWidth === lastW) return;
      lastW = track.clientWidth;
      // Keep the first card the visitor was looking at in view.
      var firstShown = Math.max(0, slides.findIndex(function (sl) { return !sl.inert && sl.getAttribute('aria-hidden') !== 'true'; }));
      measure();
      goTo(Math.floor(firstShown / per), false);
      checkOverflow(track);
      sync();
    }
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(onResize);
      this.ro.observe(track);
    } else {
      window.addEventListener('resize', onResize);
    }
    controls && controls.classList.remove('is-pending');

    this.destroy = function () {
      clearInterval(timer);
      clearTimeout(settleTimer);
      self.ro && self.ro.disconnect();
      self.io && self.io.disconnect();
    };
  }

  /* ---------------------------------------------------------------- section */
  function render(row, kind, reviews, n, cfg) {
    var track = row.querySelector('[data-adg-rv-track]');
    var uid = 'adg-rv-' + (++uidSeq);
    track.textContent = '';
    reviews.slice(0, n).forEach(function (r, i) { track.appendChild(buildSlide(r, i, n, kind, cfg, uid)); });
    track.removeAttribute('aria-busy');
    checkOverflow(track);
    watchSpacing();
    return new Carousel(row, cfg);
  }

  function fail(row, kind) {
    if (kind === 'google') { row.hidden = true; return; }
    var carousel = row.querySelector('.adg-reviews__carousel');
    if (carousel) carousel.hidden = true;
    var note = row.querySelector('.adg-reviews__note');
    if (note) note.hidden = true;
  }

  function initSection(root) {
    if (root.__adgRv) return;
    var cfg = {
      count: Math.max(1, parseInt(root.getAttribute('data-count'), 10) || 9),
      minRating: parseInt(root.getAttribute('data-min-rating'), 10) || 0,
      photos: root.getAttribute('data-photos') !== '0',
      autoplay: parseInt(root.getAttribute('data-autoplay'), 10) || 0,
      style: root.getAttribute('data-style') === 'strict' ? 'strict' : 'datamax',
      logos: root.getAttribute('data-logos') !== '0',
      gBadge: root.getAttribute('data-g-badge'),
      storeBadge: root.getAttribute('data-store-badge')
    };
    var state = { carousels: [], io: null };
    root.__adgRv = state;
    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-adg-rv-row]'));

    function keep(list) {
      return list.filter(function (r) {
        return r.text && String(r.text).trim() && r.rating >= Math.max(1, cfg.minRating);
      });
    }

    function start() {
      var jobs = rows.map(function (row) {
        var kind = row.getAttribute('data-adg-rv-row');
        var p = kind === 'google' ? loadGoogle(row, cfg) : loadStore(row, cfg);
        return p.then(function (list) {
          list = keep(list);
          if (!list.length) throw new Error('no reviews');
          return kind === 'store' ? attachProducts(list.slice(0, cfg.count)) : list;
        });
      });
      settle(jobs).then(function (results) {
        // Same card count in every row that loaded.
        var n = cfg.count;
        results.forEach(function (res) { if (res.ok) n = Math.min(n, res.value.length); });
        results.forEach(function (res, i) {
          var row = rows[i], kind = row.getAttribute('data-adg-rv-row');
          if (!res.ok) { fail(row, kind); return; }
          state.carousels.push(render(row, kind, res.value, n, cfg));
        });
      });
    }

    if ('IntersectionObserver' in window) {
      state.io = new IntersectionObserver(function (es) {
        if (es.some(function (e) { return e.isIntersecting; })) {
          state.io.disconnect();
          start();
        }
      }, { rootMargin: '800px 0px' });
      state.io.observe(root);
    } else {
      start();
    }
  }

  function destroySection(root) {
    var st = root && root.__adgRv;
    if (!st) return;
    st.io && st.io.disconnect();
    st.carousels.forEach(function (c) { c.destroy(); });
    root.__adgRv = null;
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-adg-reviews]').forEach(initSection);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll && e.target.querySelectorAll('[data-adg-reviews]').forEach(destroySection);
  });
})();
